import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { getClients } from "@/services/clientService";
import { CACHE_TTL } from "@/config/cache";

export interface ClientOption {
  id: number;
  name: string;
  email?: string;
  company_name?: string;
  services?: string[];
}

let clientsInFlightPromise: Promise<ClientOption[]> | null = null;
let lastClientsFetchTime = 0;

export const useClientStore = defineStore("clientStore", () => {
  const clients = ref<ClientOption[]>([]);
  const selectedClientId = ref<number>(1);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);

  const selectedClient = computed(() => {
    return clients.value.find((c) => Number(c.id) === Number(selectedClientId.value)) || null;
  });

  async function fetchClients(forceRefresh = false): Promise<ClientOption[]> {
    const isStale = Date.now() - lastClientsFetchTime > CACHE_TTL.clients;

    if (clients.value.length > 0 && !forceRefresh && !isStale) {
      return clients.value;
    }

    if (clientsInFlightPromise) {
      return clientsInFlightPromise;
    }

    if (clients.value.length === 0) {
      loading.value = true;
    }

    clientsInFlightPromise = (async () => {
      try {
        const res = await getClients();
        const list = Array.isArray(res)
          ? res
          : Array.isArray((res as any)?.data)
          ? (res as any).data
          : [];

        const normalized: ClientOption[] = list.map((item: any) => ({
          id: Number(item.id || item.client_id || 1),
          name: item.company_name || item.name || `Client #${item.id}`,
          email: item.email || "",
          company_name: item.company_name || item.name || "",
          services: item.services || ["overview", "social_media", "seo", "paid_ads", "website_analytics", "leads"],
        }));

        if (normalized.length > 0) {
          clients.value = normalized;
        } else if (clients.value.length === 0) {
          clients.value = [
            {
              id: 1,
              name: "Hayami Spa Beauty & Wellness",
              company_name: "Hayami Spa Beauty & Wellness",
              email: "contact@hayamispa.com",
              services: ["overview", "social_media", "seo", "paid_ads", "website_analytics", "leads"],
            },
          ];
        }

        lastClientsFetchTime = Date.now();
        error.value = null;
        return clients.value;
      } catch (err: any) {
        console.warn("[clientStore] Failed to fetch clients:", err);
        error.value = "Failed to load client options.";
        return clients.value;
      } finally {
        loading.value = false;
        clientsInFlightPromise = null;
      }
    })();

    return clientsInFlightPromise;
  }

  function setSelectedClientId(id: number) {
    if (selectedClientId.value !== id) {
      selectedClientId.value = id;
    }
  }

  function invalidateClients() {
    lastClientsFetchTime = 0;
  }

  return {
    clients,
    selectedClientId,
    selectedClient,
    loading,
    error,
    fetchClients,
    setSelectedClientId,
    invalidateClients,
  };
});
